import i18n from "@/config/translation";

type ApiErrorBody = {
  customMessage?: string;
  message?: string;
  errorKey?: string;
  errorParams?: Record<string, string>;
};

type ApiErrorToast = {
  title?: string;
  message?: string;
  translatedTitle?: string;
  translatedMessage?: string;
  translatedParams?: Record<string, string>;
  color: "error";
  duration: number;
};

const API_ERROR_TOASTS: Record<
  string,
  Pick<ApiErrorToast, "translatedTitle" | "translatedMessage">
> = {
  TIMESHEET_DAYS_LOCKED: {
    translatedTitle: "components.timesheet.monthTimesheetBooking.errors.daysLocked",
    translatedMessage: "components.timesheet.monthTimesheetBooking.errors.daysLockedHint",
  },
  TIMESHEET_CLOSED: {
    translatedTitle: "components.timesheet.monthTimesheetBooking.errors.closed",
    translatedMessage: "components.timesheet.monthTimesheetBooking.errors.closedHint",
  },
  TIMESHEET_BOOKING_VALIDATED: {
    translatedTitle: "components.timesheet.monthTimesheetBooking.errors.bookingValidated",
    translatedMessage: "components.timesheet.monthTimesheetBooking.errors.bookingValidatedHint",
  },
  TIMESHEET_FORBIDDEN: {
    translatedTitle: "components.timesheet.monthTimesheetBooking.errors.forbidden",
    translatedMessage: "components.timesheet.monthTimesheetBooking.errors.forbiddenHint",
  },
  E_SIGNATURE_NOT_SUBSCRIBED: {
    translatedTitle: "components.common.eSignatureNotSubscribed.title",
    translatedMessage: "components.common.eSignatureNotSubscribed.short",
  },
  INVALID_SIGNER_PHONE: {
    translatedTitle: "modules.resource.onboarding.signatureInvalidPhone",
    translatedMessage: "modules.resource.onboarding.signatureInvalidPhoneHint",
  },
  INVALID_SIGNER_EMAIL: {
    translatedTitle: "modules.resource.onboarding.signatureInvalidEmail",
    translatedMessage: "modules.resource.onboarding.signatureInvalidEmailHint",
  },
  INVALID_SIGNER_FIRST_NAME: {
    translatedTitle: "modules.resource.onboarding.signatureInvalidFirstName",
    translatedMessage: "modules.resource.onboarding.signatureInvalidFirstNameHint",
  },
  INVALID_SIGNER_LAST_NAME: {
    translatedTitle: "modules.resource.onboarding.signatureInvalidLastName",
    translatedMessage: "modules.resource.onboarding.signatureInvalidLastNameHint",
  },
  YOUSIGN_INVALID_PARAMS: {
    translatedTitle: "modules.resource.onboarding.signatureInvalidParams",
    translatedMessage: "modules.resource.onboarding.signatureInvalidParamsHint",
  },
  YOUSIGN_REQUEST_FAILED: {
    translatedTitle: "modules.resource.onboarding.regenerateSignatureError",
  },
  ONBOARDING_ARCHIVE_FILE_UNAVAILABLE: {
    translatedTitle: "modules.resource.onboarding.archiveError",
    translatedMessage: "modules.resource.onboarding.archiveFileUnavailable",
  },
  EMAIL_USED_BY_EMPLOYEE: {
    translatedTitle: "components.applyToJobModal.error.emailUsedByEmployee.title",
    translatedMessage: "components.applyToJobModal.error.emailUsedByEmployee.message",
  },
  SLOT_ALREADY_TAKEN: {
    translatedTitle: "components.common.meeting.sharedSlot.errors.slotAlreadyTaken",
    translatedMessage: "components.common.meeting.sharedSlot.errors.slotAlreadyTakenHint",
  },
  ALREADY_ON_SLOT: {
    translatedTitle: "components.common.meeting.sharedSlot.errors.alreadyOnSlot",
  },
  SHARED_SLOT_EVALUATED: {
    translatedTitle: "components.common.meeting.sharedSlot.errors.evaluated",
    translatedMessage: "components.common.meeting.sharedSlot.errors.evaluatedHint",
  },
  SHARED_SLOT_NOT_EXCHANGEABLE: {
    translatedTitle: "components.common.meeting.sharedSlot.errors.notExchangeable",
  },
  SHARED_SLOT_PAST: {
    translatedTitle: "components.common.meeting.sharedSlot.errors.past",
  },
  SHARED_SLOT_NOT_FOUND: {
    translatedTitle: "components.common.meeting.sharedSlot.errors.notFound",
  },
  SHARED_SLOT_FULL: {
    translatedTitle: "components.common.meeting.sharedSlot.errors.full",
  },
  SHARED_SLOT_NOT_STARTED: {
    translatedTitle: "components.common.meeting.sharedSlot.errors.notStarted",
  },
  // A candidate booking an interview from their space
  SLOT_NOT_AVAILABLE: {
    translatedTitle: "modules.recruitment.careerCandidateSpace.booking.errors.slotNotAvailable",
    translatedMessage: "modules.recruitment.careerCandidateSpace.booking.errors.chooseAnother",
  },
  ALREADY_BOOKED: {
    translatedTitle: "modules.recruitment.careerCandidateSpace.booking.errors.alreadyBooked",
  },
  BOOKING_CLOSED: {
    translatedTitle: "modules.recruitment.careerCandidateSpace.booking.errors.bookingClosed",
  },
  BOOKING_CONFLICT: {
    translatedTitle: "modules.recruitment.careerCandidateSpace.booking.errors.bookingClosed",
  },
  TOO_MANY_CANDIDATE_MESSAGES: {
    translatedTitle: "modules.recruitment.careerCandidateSpace.messages.errors.tooMany",
  },
  INVALID_CANDIDATE_MESSAGE: {
    translatedTitle: "modules.recruitment.careerCandidateSpace.messages.errors.invalid",
  },
};

const looksLikeRawApiPayload = (text?: string) => {
  if (!text) return false;
  const trimmed = text.trim();
  return (
    trimmed.startsWith("{") ||
    trimmed.includes("invalid_params") ||
    trimmed.includes("parameters_not_valid") ||
    trimmed.includes("Signer creation failed")
  );
};

export const inferApiErrorKey = (data?: unknown): string | undefined => {
  if (!data || typeof data !== "object") return undefined;
  const body = data as ApiErrorBody;
  if (body.errorKey) return body.errorKey;

  const text = `${body.customMessage || ""} ${body.message || ""}`;
  if (!text.trim()) return undefined;
  if (/phone_number|valid phone number/i.test(text)) return "INVALID_SIGNER_PHONE";
  if (/info\(email\)|valid email/i.test(text)) return "INVALID_SIGNER_EMAIL";
  if (/first_name/i.test(text)) return "INVALID_SIGNER_FIRST_NAME";
  if (/last_name/i.test(text)) return "INVALID_SIGNER_LAST_NAME";
  if (/parameters_not_valid|invalid_params/i.test(text)) return "YOUSIGN_INVALID_PARAMS";
  if (looksLikeRawApiPayload(text)) return "YOUSIGN_REQUEST_FAILED";
  return undefined;
};

export const getApiErrorToast = (data?: unknown): ApiErrorToast | null => {
  if (!data || typeof data !== "object") return null;
  const body = data as ApiErrorBody;

  const mapped = API_ERROR_TOASTS[inferApiErrorKey(body) || ""];
  if (mapped) {
    return {
      ...mapped,
      translatedParams: body.errorParams,
      color: "error",
      duration: 8000,
    };
  }

  if (body.customMessage && !looksLikeRawApiPayload(body.customMessage)) {
    return {
      title: body.customMessage,
      message: body.message ?? i18n.global.t("services.api.errors.contactSupport"),
      color: "error",
      duration: 8000,
    };
  }

  return null;
};

export const hasApiErrorToast = (error: unknown): boolean => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  return Boolean(getApiErrorToast(data));
};
